import { ErrorCode } from '../constants/src';

export interface AppExceptionDetail {
  code: ErrorCode;
  message: string;
  field?: string;
  params?: Record<string, unknown>;
}

export interface IAppException {
  code: ErrorCode;
  message: string;
  details: AppExceptionDetail[];
  timestamp: string;

  getStatus(): number;
  getResponse(): object;
}

export interface AppExceptionOptions {
  code?: string;
  parentCode?: string;
  message?: string;
  status?: number;
  field?: string;
  params?: Record<string, unknown>;
  details?: AppExceptionDetail[];
}

export class AppException extends Error implements IAppException {
  public readonly code: ErrorCode;
  public readonly status: number;
  public readonly details: AppExceptionDetail[];
  public readonly body?: unknown;
  public readonly query?: unknown;
  public readonly timestamp: string;

  constructor({
    code = ErrorCode.UNKNOWN_ERROR,
    message = 'An unexpected error occurred.',
    status = 500,
    field,
    params,
    parentCode,
    details,
  }: AppExceptionOptions) {
    super(message);

    this.code = (parentCode ?? code) as ErrorCode;
    this.status = status;
    this.timestamp = new Date().toISOString();

    Object.setPrototypeOf(this, new.target.prototype);
    this.name = this.constructor.name;

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }

    this.details = details ?? [
      {
        code: code as ErrorCode,
        message,
        ...(field ? { field } : {}),
        ...(params ? { params } : {}),
      },
    ];
  }

  getStatus(): number {
    return this.status;
  }

  getResponse(): object {
    return {
      status: this.status,
      code: this.code,
      details: this.details,
    };
  }

  static create(options: AppExceptionOptions): AppException {
    return new AppException(options);
  }
}
